/**
 * Check if the current page is the given route
 * @param {String} route
 * @returns {Boolean}
 */
export function currentPageIs(route) {
  return (
    window.location.pathname === route ||
    window.location.pathname === `${route}/`
  );
}

export function isGenericB2BPage() {
  return isGenericPage() && !window.location.search;
}

export function isGenericRxpPage() {
  const source = new URLSearchParams(window.location.search).get("source");
  return isGenericPage() && source === "rxp";
}

export function isGenericPatientenPage() {
  const source = new URLSearchParams(window.location.search).get("source");
  return isGenericPage() && source === "patienten";
}

/**
 * Check if the current page is a Generic page.
 * A generic page is a page that is not a "B2B", "Patienten" or "Rezept Express" page,
 * but based on the "source" query param, we can determine which navbar items to show.
 * If there is no "source" query param, we show the default navbar items (B2B items).
 *
 * /ueber-anni?source=rxp, /presse?source=patienten
 * @returns {Boolean}
 */
export function isGenericPage() {
  const route = window.location.pathname;

  const routes = [
    "/ueber-anni",
    "/kontakt",
    "/presse",
    "/datenschutz",
    "/impressum",
  ];

  return (
    routes.includes(route) || routes.some((r) => route.startsWith(`${r}/`))
  );
}

/**
 * Check if the current page is a "B2B" page
 * @returns {Boolean}
 */
export function isB2BRoute() {
  const route = window.location.pathname;

  const routes = ["/", "/module", "/mehrwert"];

  return (
    routes.includes(route) ||
    routes.some((r) => route.startsWith(`${r}/`)) ||
    isGenericB2BPage()
  );
}

/**
 * Check if the current page is a "Patienten" page
 * @returns {Boolean}
 */
export function isPatientRoute() {
  const route = window.location.pathname;

  const routes = [
    "/autoren",
    "/unsere-partner",
    "/patienten",
    "/gesundheit",
    "/pflege",
    "/hilfsmittel",
    "/dev-only",
  ];

  return (
    routes.includes(route) ||
    routes.some((r) => route.startsWith(`${r}/`)) ||
    isGenericPatientenPage()
  );
}

/**
 * Check if the current page is Rezept Express
 * @returns {Boolean}
 */
export function isRxpRoute() {
  const route = window.location.pathname;
  const routes = ["/rezept-express", "/anbieter-finder"];

  return (
    routes.includes(route) ||
    routes.some((r) => route.startsWith(`${r}/`)) ||
    isGenericRxpPage()
  );
}

/**
 * Check if the current page is from Information Portal
 * @param {Array} overviewPagesData
 * @returns {Boolean}
 */
export function isInfoPage(overviewPagesData) {
  if (!overviewPagesData) return false;
  if (!Array.isArray(overviewPagesData)) return false;

  return overviewPagesData.some((page) =>
    window.location.pathname.startsWith(`/${page.overviewStory.full_slug}`)
  );
}

/**
 * Retrieves the Call-to-Action (CTA) data based on the current route.
 * @returns An object containing the CTA URL and target, or null if no matching route is found.
 */
export function getCtaData() {
  if (isB2BRoute()) {
    return {
      url: "https://connect.anni.care",
      target: "_blank",
    };
  }

  if (currentPageIs("/rezept-express")) {
    return {
      url: "/empfehlungs-app",
      target: "_self",
    };
  }

  return null;
}

/**
 * Determines the starting page URL based on the current route.
 * @returns The URL of the starting page.
 */
export function getStartPageUrl() {
  return isPatientRoute()
    ? "/patienten"
    : isRxpRoute()
    ? "/rezept-express"
    : "/";
}

/**
 * Builds a URL with the source query parameter based on the current route.
 * @param {String} url The base URL.
 * @returns The URL, potentially with the source query parameter.
 */
export function buildUrlWithSource(url) {
  const source = isPatientRoute() ? "patienten" : isRxpRoute() ? "rxp" : null;

  const urlObject = new URL(url, window.location.origin);
  if (source) urlObject.searchParams.set("source", source);

  return urlObject.toString().replace(window.location.origin, "");
}
